import { cloneElement, createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import OrganizationIcon from '~/assets/OrganizationIcon';
import { AudienceCondition, ConversationReferenceType } from '~/types/api.graphql';
import { conditionIconsMap } from '~/utils/audience';

/**
 * Matches mention markdown `[name](amp://audiences/<id>?...)` or
 * `[name](amp://organizations/<id>?...)`.  The deep-link path supports an
 * optional single sub-path segment (e.g. `/settings`) but rejects deeper
 * nesting like `/integrations/456`.
 *
 * Group 1 → name, Group 2 → full deep link.
 */
export const MENTION_MARKDOWN_REGEX =
  /\[([^\]]+)\]\((amp:\/\/(?:audiences|organizations)\/[^/?#)]+(?:\/[^/?#)]+)?(?:\?[^)]*)??)\)/g;

// ---------------------------------------------------------------------------
// SVG icon strings (14×14, stroke="currentColor") for each audience condition
// and for organizations, derived from the shared icon components in ~/assets.
// ---------------------------------------------------------------------------

const ICON_PROPS = { width: 14, height: 14, stroke: 'currentColor' };

const ICON_ORGANIZATION = renderToStaticMarkup(createElement(OrganizationIcon, ICON_PROPS));

const CONDITION_ICON_MAP = Object.fromEntries(
  Object.entries(conditionIconsMap).map(([condition, element]) => [
    condition,
    renderToStaticMarkup(cloneElement(element, ICON_PROPS)),
  ]),
) as Record<AudienceCondition, string>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract audienceCondition from a deep-link URL (amp://audiences/…?audienceCondition=X). */
const getConditionFromDeepLink = (deepLink: string): AudienceCondition | null => {
  try {
    const params = new URL(deepLink).searchParams;
    const raw = params.get('audienceCondition');
    if (raw && Object.values(AudienceCondition).includes(raw as AudienceCondition)) {
      return raw as AudienceCondition;
    }
  } catch {
    // malformed URL — fall through
  }
  return null;
};

const isOrganizationDeepLink = (deepLink: string) => deepLink.startsWith('amp://organizations');

const ICON_GLOBAL_FALLBACK = CONDITION_ICON_MAP[AudienceCondition.Global];

const getIconSvg = (audienceCondition: AudienceCondition | null, isOrg: boolean): string => {
  if (isOrg) return ICON_ORGANIZATION;
  if (audienceCondition) {
    return CONDITION_ICON_MAP[audienceCondition] ?? ICON_GLOBAL_FALLBACK;
  }
  return ICON_GLOBAL_FALLBACK;
};

// ---------------------------------------------------------------------------
// DOM chip builder (used in the contenteditable editor)
// ---------------------------------------------------------------------------

export interface BuildChipOptions {
  name: string;
  deepLink: string;
  audienceCondition?: AudienceCondition | null;
  type?: ConversationReferenceType | null;
}

export const MENTION_CHIP_CLASS = 'mention-chip';
export const MENTION_CHIP_ORG_MODIFIER = 'mention-chip--org';

/** Class applied to the in-progress chip span shown while the user types @query. */
export const PENDING_CHIP_CLASS = 'mention-chip-pending';

const MAX_NAME_LENGTH = 40;

/** Truncate a display name to MAX_NAME_LENGTH characters. */
export const truncateName = (name: string): string =>
  name.length > MAX_NAME_LENGTH ? `${name.slice(0, MAX_NAME_LENGTH)}…` : name;

/**
 * Creates a contenteditable-false chip DOM node for use inside the
 * mention editor.  The chip contains an icon, the name, and a × remove
 * button.  The remove button is detected via class `chip-remove` in the
 * editor's onMouseDown handler.
 */
export const buildChipElement = ({ name, deepLink, audienceCondition, type }: BuildChipOptions): HTMLSpanElement => {
  const isOrg: boolean = type === ConversationReferenceType.Organization || isOrganizationDeepLink(deepLink);
  const condition = audienceCondition ?? getConditionFromDeepLink(deepLink);
  const iconSvg: string = getIconSvg(condition, isOrg);

  const displayName = truncateName(name);

  const chip = document.createElement('span');
  chip.contentEditable = 'false';
  chip.className = isOrg ? `${MENTION_CHIP_CLASS} ${MENTION_CHIP_ORG_MODIFIER}` : MENTION_CHIP_CLASS;
  chip.dataset.deeplink = deepLink;
  chip.dataset.name = name;

  const iconEl = document.createElement('span');
  iconEl.className = 'chip-icon';
  iconEl.setAttribute('aria-hidden', 'true');
  iconEl.innerHTML = iconSvg; // safe: iconSvg comes from our known constant map

  const nameEl = document.createElement('span');
  nameEl.className = 'chip-name';
  nameEl.textContent = displayName; // textContent avoids XSS

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'chip-remove';
  removeBtn.setAttribute('tabindex', '-1');
  removeBtn.textContent = '×';

  chip.append(iconEl, nameEl, removeBtn);
  return chip;
};

// ---------------------------------------------------------------------------
// HTML rendering — read-only chips for the user message bubble
// ---------------------------------------------------------------------------

/** Escape a string for safe use as HTML text content. */
export const escapeHtml = (str: string) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

/**
 * Pre-processes a message body (markdown) so that `[name](deepLink)` mention
 * syntax renders as an orange chip span rather than a plain hyperlink.
 * Call this before passing the body to `marked.parse()` for user messages.
 */
export const renderBodyWithChips = (text: string): string =>
  // Regex captures are typed as `any[]` by the JS stdlib — convert explicitly
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  text.replace(MENTION_MARKDOWN_REGEX, (_, rawName, rawDeepLink) => {
    const name = truncateName(String(rawName));
    const deepLink = String(rawDeepLink);
    const isOrg: boolean = isOrganizationDeepLink(deepLink);
    const condition = getConditionFromDeepLink(deepLink);
    const iconSvg: string = getIconSvg(condition, isOrg);
    const safeName: string = escapeHtml(name);
    const orgClass = isOrg ? ` ${MENTION_CHIP_ORG_MODIFIER}` : '';
    return `<span class="mention-chip${orgClass}"><span class="chip-icon" aria-hidden="true">${iconSvg}</span><span class="chip-name">${safeName}</span></span>`;
  });

/**
 * Converts an amp:// deep link to an in-app route that can be passed to
 * React Router's navigate() / used as an <a href>.
 * Returns null for unrecognised deep-link schemas.
 */
export const deepLinkToAppPath = (deepLink: string): string | null => {
  const ampPrefix = 'amp://';
  if (!deepLink.startsWith(ampPrefix)) return null;

  const withoutProtocol = deepLink.slice(ampPrefix.length);
  const queryStart = withoutProtocol.indexOf('?');
  const pathPart = queryStart >= 0 ? withoutProtocol.slice(0, queryStart) : withoutProtocol;

  const processPath = (type: 'audiences' | 'organizations', defaultSubPath: string): string | null => {
    const prefix = `${type}/`;
    if (pathPart.startsWith(prefix)) {
      const rest = pathPart.slice(prefix.length);
      const segments = rest.split('/');
      if (segments.length > 2) return null;
      const [id, subPath] = segments;
      return `/app/${type}/${id}/${subPath ?? defaultSubPath}`;
    }
    return null;
  };
  const audiencePath = processPath('audiences', 'overview');
  if (audiencePath) return audiencePath;
  const organizationPath = processPath('organizations', 'general');
  if (organizationPath) return organizationPath;

  return null;
};

/**
 * Like renderBodyWithChips but wraps each chip in an `<a href>` pointing to
 * the in-app route for the referenced entity.  Falls back to a plain span
 * for deep links whose schema is not recognised.
 * Use this for read-only chat messages where chips should be clickable links.
 */
export const renderBodyWithChipLinks = (text: string): string =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  text.replace(MENTION_MARKDOWN_REGEX, (_, rawName, rawDeepLink) => {
    const name = truncateName(String(rawName));
    const deepLink = String(rawDeepLink);
    const isOrg: boolean = isOrganizationDeepLink(deepLink);
    const condition = getConditionFromDeepLink(deepLink);
    const iconSvg: string = getIconSvg(condition, isOrg);
    const safeName: string = escapeHtml(name);
    const appPath = deepLinkToAppPath(deepLink);
    const orgClass = isOrg ? ` ${MENTION_CHIP_ORG_MODIFIER}` : '';

    if (appPath) {
      const safeHref = escapeHtml(appPath);
      return `<a href="${safeHref}" class="mention-chip${orgClass}"><span class="chip-icon" aria-hidden="true">${iconSvg}</span><span class="chip-name">${safeName}</span></a>`;
    }

    return `<span class="mention-chip${orgClass}"><span class="chip-icon" aria-hidden="true">${iconSvg}</span><span class="chip-name">${safeName}</span></span>`;
  });

// ---------------------------------------------------------------------------
// Title utility
// ---------------------------------------------------------------------------

/**
 * Strips mention markdown `[name](deepLink)` → `name` so the conversation
 * title only contains plain human-readable text.
 */
export const stripMentionMarkdown = (text: string): string => text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
